define((require) => {
    "use strict";

    const Action           = require("shared/ui/experience/action/Action");
    const ParametersHelper = require("shared/ui/experience/action/ParametersHelper");
    const {isBlank}        = require("shared/util/StringUtils");
    const ViewerEvents     = require("shared/services/ViewerEvents");
    const Window           = require("shared/services/Window");
    const {each}           = require("underscore");

    // Register to search for illegal characters in parameters names and values, matching a similar one in the API...
    const PARAMETER_NAME  = /([^a-zA-Z0-9_\- ])/; // TODO: we should disallow pre and postfixed spaces, like /(^\s|\s$|[^a-zA-Z0-9_\- ])/
    const PARAMETER_VALUE = /([^a-zA-Z0-9_.*+\-:/\\=?#&])/;

    /**
     * Action that executes a custom javascript action on the top most reachable (client) page.
     *
     * @class CustomJs
     * @constructor
     */
    const CustomJsAction = Action.extend("CustomJSAction", { // TODO: why no extend JsAction with a bit of change?

        // Note: "clientSpace" is not set to true to allow users to enter stuff like "alert(#{foo})" as well.

        defaults: {
            actionId:   null, // TODO: doc
            name:       "Untitled Custom JavaScript",
            code:       "",
            parameters: {}, // Map<String,String> of parameter names (and associated values), e.g. {"Product ID": "abc"}
            usedBy:     [] // array of experience ids
        },

        _event: ViewerEvents.JAVASCRIPT_EXECUTE,

        _activateable() {
            if (!this.get("code")) {
                return "Missing code";
            }
            return this.checkCode() ? "Code not runnable" : null;
        },

        /**
         * @protected
         * Activates this Custom JS action. <p>
         *
         * A JS action can ever only run when the owning Experience is embedded via channels or we will get
         * cross domain issues.
         *
         * @return {*} The result of running the code; can be falsey.
         */
        _activate() {
            Window.run(this.getRunnableCode());
            return Object.assign({actionId: this.id, widgetId: this.attributes.widgetId}, {data: {name: this.get("name")}});
        },

        /**
         * Merges the parameter values into the code template and returns the resulting code.
         *
         * @return {String} The runnable code as a string.
         */
        getRunnableCode() {
            return ParametersHelper.inject(this.get("code"), this.get("parameters"));
        },

        /**
         * Checks if the current code set for this model is valid/runnable, returning null if valid
         * or the found error.
         *
         * @return {Error} The error if invalid; otherwise null.
         */
        checkCode() {
            return CustomJsAction.checkRunnableCode(this.get("code"), this.get("parameters"));
        }

    }, {
        TYPE: "customJs",

        /**
         * Checks if the supplied code template is valid/runnable for the supplied parameters, returning null if valid.
         *
         * @param  {String}  template    The code template to check after <tt>parameters</tt> have been injected.
         * @param  {Map}    [parameters] The parameters to optionally check; can be undefined.
         *
         * @return {Error} The error if invalid; otherwise null.
         */
        checkRunnableCode(template, parameters) {
            try {
                // Parameter names and values must be valid...
                if (parameters) {
                    each(parameters, (value, name) => {
                        const error = CustomJsAction.checkParameter(name, value);
                        if (error) {
                            throw error;
                        }
                    });
                }

                // Inject any arguments, allowing them to be empty...
                const code = ParametersHelper.inject(template, parameters);

                // eslint-disable-next-line no-new, no-new-func
                new Function(code);

                // And JS must be able to be serialized to/from JSON...
                const result = JSON.parse(JSON.stringify({code}));

                if (result.code !== code) {
                    throw new Error("Code cannot be serialized to/from json.");
                }
                return null; // no error
            } catch (error) {
                return error;
            }
        },

        /**
         * Checks the specified parameter name and value and returns an error message if illegal,
         * otherwise returns null.
         *
         * @param  {String} name    The parameter name to check; cannot be empty.
         * @param  {String} [value] The parameter value to check; can be empty or not supplied at all, e.g. for
         *                          validation in the Editor when the Custom JS Action prototype is set up.
         *
         * @return {String} Returns null if not error, otherwise an error message.
         */
        checkParameter(name, value) {
            let match = PARAMETER_NAME.exec(name);
            if (match) {
                return `Invalid parameter name: '${match[1]}' in '${name}'`;
            } else if (isBlank(name)) {
                return "Parameter name cannot be empty";
            }

            if (arguments.length > 1) {
                match = PARAMETER_VALUE.exec(value);
                if (match) {
                    return `Invalid parameter value: '${match[1]}' in ${value}`;
                }
            }
            return null;
        }
    });

    return CustomJsAction;
});



// WEBPACK FOOTER //
// ./src/shared/ui/experience/action/customJs/CustomJsAction.js